<template>
  <!-- RESULTS -->
  <div>
    <!-- FILTER -->
    <div class="sm:mb-16">
      <!-- Search Input -->
      <div id="search-input" v-animate-in-from-bottom class="relative">
        <input
          id="search"
          ref="searchInput"
          class="peer mb-7 block w-full rounded-sm bg-grey-100 p-3 outline-none transition sm:p-5"
          :placeholder="`${$t('search.search')}...`"
          @input="width >= 768 ? searchDebounced($event.target?.value) : null"
          @keydown.enter="
            width < 768
              ? [(filter.searchInput = $event.target?.value), doSearch()]
              : null
          "
        />
        <button
          type="button"
          class="
            absolute right-5 top-1/2 size-8 -translate-y-1/2 transition-transform
            hover:scale-125
            peer-placeholder-shown:hidden
          "
          :aria-label="$t('search.clear_search_input')"
          @click="onClickClearSearch()"
        >
          <IconPlus class="size-full rotate-45 p-1" />
        </button>
      </div>

      <!-- CATEGORY FILTER -->
      <div v-animate-children-in-from-left class="flex flex-wrap gap-x-6 gap-y-3">
        <div class="mt-3.5 typo-label-md sm:mt-3 sm:typo-label-lg">
          {{ $t('filter') }}
        </div>
        <ul class="flex flex-wrap gap-x-3 gap-y-2">
          <li v-for="category in categories" :key="category?.id">
            <button
              :class="filter?.category === category?.id ? 'bg-blue-500 text-white hover:bg-blue-600':`
                bg-grey-100
                hover:bg-grey-200
              `"
              class="rounded px-4 py-2 transition-colors typo-button-text-xl"
              type="button"
              @click="onClickCategory(category)"
            >
              {{ category?.title }}
            </button>
          </li>
        </ul>
      </div>

      <!-- HITS -->
      <div>
        <div v-show="result?.nbHits > 0 && !hideResults">
          <ul class="grid divide-y">
            <SearchResult
              v-for="page in paginatedHits"
              :key="page.objectID"
              :data="page"
              :show-documents="filter.category === 'documents'"
              :search-query="filter.searchInput"
            />
          </ul>
        </div>

        <!-- PAGINATION -->
        <PaginationSearch
          v-if="result?.nbHits > 0 && !hideResults"
          :items-per-page="8"
          :data="result?.hits"
          scroll-to-id="search-input"
          should-scroll
          @update="paginatedHits = $event"
        />

        <!-- NO RESULTS -->
        <Transition name="fade-in">
          <NoEntriesFound v-if="result?.nbHits === 0" class="mt-10" />
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// VARIABLES
const algoliaIndexPrefix = useRuntimeConfig().public.ALGOLIA_SCOUT_PREFIX
if (!algoliaIndexPrefix) {
  throw new Error('ALGOLIA_SCOUT_PREFIX not set in runtime config')
}

const { locale, t } = useI18n()

const { width } = useWindowSize()

const searchInput = ref<HTMLInputElement | null>(null)
const route = useRoute()
const params = useUrlSearchParams('history')

const hideResults = ref(true)
const { result, search } = useAlgoliaSearch(`${algoliaIndexPrefix}default`)

const paginatedHits = ref([])

const categories = [
  {
    id: 'documents',
    title: t('search.documents'),
  },
]

// Define the filter
const filter = ref({
  searchInput: '',
  category: '',
})

// Get the filter from the URL
const queryFilter = route.query.search
if (queryFilter) {
  filter.value = JSON.parse(queryFilter)
  // Ensure valid values
  if (!filter.value.searchInput) {
    filter.value.searchInput = ''
  }

  if (!filter.value.category) {
    filter.value.category = ''
  }
}

// FUNCTIONS
async function doSearch() {
  // If no search query is defined, hide the results
  if (!filter.value.searchInput && !filter.value.category) {
    hideResults.value = true
    return
  }

  const facetFilters = [`locale:${locale.value}`]

  if (filter.value.category) {
    facetFilters.push(`has_downloads:true`)
  }

  await search({
    query: filter.value.searchInput,
    requestOptions: {
      facetFilters: facetFilters,
      restrictSearchableAttributes: filter.value.category ? ['file_names'] : undefined,
    },
  })

  hideResults.value = false
}

const searchDebounced = useDebounceFn((value) => {
  filter.value.searchInput = value
  doSearch()
}, 200)

async function onClickClearSearch() {
  filter.value.searchInput = ''
  if (!searchInput.value) return
  searchInput.value.value = ''
  searchInput.value.focus()

  doSearch()
}

function onClickCategory(category: {
  id: string
  title: string
}) {
  filter.value.category = filter.value.category === category.id ? '' : category.id

  if (filter.value.category) {
    doSearch()
  } else if (filter.value.searchInput) {
    doSearch()
  } else {
    // If no search input is defined, hide the results
    hideResults.value = true
  }
}

// WATCH
watch(
  () => filter.value,
  (newVal) => {
    // Save the filter in the URL
    params['search'] = JSON.stringify(newVal)
  },
  { deep: true },
)

// INITIAL SEARCH
if (filter.value.searchInput || filter.value.category) {
  doSearch()
}

onMounted(() => {
  searchInput.value.value = filter.value.searchInput
})
</script>

<style>
.highlight {
  em {
    @apply bg-grey-200 py-1 not-italic rounded-sm;
  }
}
</style>
